<template>
  <div v-if="lines.length" class="ledger">
    <!-- Named on its border, like a fieldset's legend: unlabelled, the one
         line read as another action button (playtest 11, Matt on the
         Frontier: "there should be a little log that shows you what you've
         done" - it was there). Costs no height. -->
    <span class="ledger__head">{{ role ? 'What you did' : 'Log' }}</span>
    <ul class="ledger__list">
      <li v-for="(e, i) in recent" :key="e.seq" class="ledger__line" :class="{ 'ledger__line--bad': bad(e) }">
        <!-- An act drawn in parts; anything else (and any old save whose
             entries predate the parts) falls back to its own sentence. -->
        <template v-if="e.label">
          <span class="ledger__what"><small v-if="role && e.role !== role" class="ledger__who" :style="{ color: roleColour(e.role) }">{{ roleLabel(e.role) }} · </small>{{ e.label }}</span>
          <span v-if="e.rolls && e.rolls.length" class="ledger__dice">
            <Die3d
              v-for="(d, j) in e.rolls"
              :key="j"
              :value="d"
              :faces="e.sides"
              :phase="rolling && rolling.go && rolling.seq === e.seq ? 'tumble' : null"
              :duration="rolling && rolling.seq === e.seq ? rolling.each[j] : 900"
            />
          </span>
          <span v-if="pips(e.paid, e.role).length" class="ledger__paid">
            <Pip v-for="p in pips(e.paid, e.role)" :key="`p${p.kind}`" :kind="p.kind" :n="p.n" />
          </span>
          <span v-if="pips(e.gained).length" class="ledger__got">
            <Pip v-for="p in pips(e.gained)" :key="`g${p.kind}`" :kind="p.kind" :n="p.n" signed />
          </span>
          <span v-else class="ledger__told">{{ told(e) }}</span>
        </template>
        <span v-else class="ledger__told ledger__told--wide">{{ e.text }}</span>
      </li>
    </ul>
    <details v-if="earlier.length" class="ledger__more">
      <summary>Earlier ({{ earlier.length }})</summary>
      <ul class="ledger__list">
        <li v-for="e in earlier" :key="e.seq" class="ledger__line">
          <span class="ledger__told ledger__told--wide">{{ e.text }}</span>
        </li>
      </ul>
    </details>
  </div>
</template>

<script>
import Pip from './Pip.vue';
import Die3d from './Die3d.vue';
import { COUNTED } from '@/engine/effects';
import { SPENDABLE } from './resourceArt';
import { ROLE_INFO } from '@/engine/constants';

// The stores every role draws on: another role spending these shows in your log too.
const SHARED = ['food', 'goods', 'coin', 'knowledge'];

// What has happened, newest first: the last few lines drawn in the pips
// everything else is priced in, and the rest a tap away. Matt, 2026-09-19:
// "let's have it so that the log shows the last two or three things and then
// can be expanded to see more."
//
// It replaced a band that showed only your own last action - which read as
// a caption nobody could place. This is the phone's ONE log; the collapsed
// list that used to sit at the foot of the screen is gone.
const ORDER = [...SPENDABLE, ...COUNTED];
// Two lines, so a long game's log cannot push the Done button off the
// bottom of the board. Matt, 2026-09-19: "let's drop the log to two
// visible lines." The rest is one tap away under Earlier.
const SHOWN = 2;
// How long after a board appears its dice stay where they landed.
const SETTLE_IN = 600;
const DEEP = 24;

export default {
  name: 'Ledger',
  components: { Die3d, Pip },
  props: {
    log: { type: Array, default: () => [] },
    // Whose log this is. Matt, 2026-09-19: "the log doesn't need to show
    // the entire game's log, just the log for the Frontier player." Empty
    // means everything, which is what the table would want.
    role: { type: String, default: '' },
    // Can the newest line be taken back? It is always the newest: the
    // reducer keeps one step of undo, for the role that took it.
    undoable: { type: Boolean, default: false },
  },
  emits: ['undo'],
  data() {
    // Which line's dice are in the air, and how long each of them takes.
    return { rolling: null };
  },
  watch: {
    // A new roll: throw them. Every die gets its own length, so four dice
    // land at four different moments - the lesson cant-stop learned when
    // they all stopped together and it read as a glitch.
    // Watch the SEQ, not the entry. Every sync rebuilds the log out of
    // JSON, so the objects are new even when nothing happened - and on a
    // reload the whole finished log arrives at once. Neither is a throw.
    // The cost is that the first dice a role rolls in a session do not
    // tumble, which is a fair price for not re-rolling them on every load.
    newestSeq: {
      immediate: false,
      handler(now, was) {
        if (now == null || was == null || now <= was) return;
        // And nothing throws in the first moments of a board's life. A
        // page that loads, a room that rejoins and a tab that is tapped
        // can all hand us a finished log in more than one step, and no
        // reachable tap happens this fast. Matt saw the last roll replay
        // itself on load twice; this is the belt to the seq check's braces.
        if (Date.now() - this.bornAt < SETTLE_IN) return;
        const entry = this.newest;
        if (!entry || !entry.rolls || !entry.rolls.length) return;
        if (this.reduced) return;
        const each = entry.rolls.map(() => 800 + Math.round(Math.random() * 700));
        // Mount them at REST, then start them a tick later. These dice are
        // created fresh with every roll - cant-stop's four live in a tray
        // and persist - and Die3d only tumbles when `phase` CHANGES, so a
        // die born mid-tumble just sat there bobbling.
        this.rolling = { seq: entry.seq, each, go: false };
        this.$nextTick(() => {
          if (this.rolling && this.rolling.seq === entry.seq) this.rolling = { ...this.rolling, go: true };
        });
        window.clearTimeout(this.settle);
        this.settle = window.setTimeout(() => { this.rolling = null; }, Math.max(...each) + 250);
      },
    },
  },
  async created() {
    this.bornAt = Date.now();
    const { prefersReducedMotion } = await import('@/utils/eventTheatre');
    this.reduced = prefersReducedMotion();
  },
  beforeUnmount() { window.clearTimeout(this.settle); },
  computed: {
    newest() { return this.recent[0] || null; },
    newestSeq() { return this.newest ? this.newest.seq : null; },
    lines() {
      // Your own role's acts - and anyone else's that took from the stores
      // everybody shares (playtest 12, Matt as the Stewards: "did somebody
      // just spend some goods?" - the Scholars' experiment had, and only
      // the Scholars' log said so).
      const shared = (l) => l.kind === 'act' && SHARED.some((k) => (l.paid || {})[k] > 0);
      // A nature the world read out is a line in the Scholars' log, with its
      // source (playtest 13: clues "don't say when they were learned").
      const learned = (l) => this.role === 'science' && l.reveals?.length && !(l.kind === 'act' && l.role === 'science');
      const mine = this.role
        ? this.log.filter((l) => l.role === this.role || shared(l) || learned(l))
          .map((l) => (learned(l) && !(l.role === this.role) ? { ...l, role: 'science', label: `Learned: ${l.reveals.map((r) => r.said).join(', ')} (from ${l.reveals[0].from})`, paid: {}, gained: {}, rolls: null } : l))
        : this.log;
      return [...mine].reverse().slice(0, DEEP);
    },
    recent() { return this.lines.slice(0, SHOWN); },
    earlier() { return this.lines.slice(SHOWN); },
  },
  methods: {
    roleLabel(r) { return ROLE_INFO[r]?.label || r; },
    roleColour(r) { return ROLE_INFO[r]?.colour || 'inherit'; },
    // A price paid in hands is shifts for the Stewards and actions for the
    // Frontier: same number, their own glyph.
    pips(patch = {}, role = null) {
      const hand = { trade: 'shifts', military: 'actions' }[role] || 'workers';
      return ORDER
        .filter((k, i) => ORDER.indexOf(k) === i && patch && patch[k])
        .map((k) => ({ kind: k === 'workers' ? hand : k, n: patch[k] }));
    },
    // A fight that fell short is not good news, and green would say it was.
    bad(e) {
      const got = this.pips(e.gained);
      return got.length > 0 && !got.some((p) => p.n > 0);
    },
    // Moving, scouting and raising an outpost shift no resources at all,
    // and they still have to say what they did.
    told(e) {
      const all = String(e.said || '');
      // A technology learned is the headline, and it comes after the first
      // comma: cutting there showed "mixes Growth and Stone: hot" and lost
      // the EUREKA the TV was celebrating (playtest oaiRw).
      if (/EUREKA/.test(all)) return `${all.split(';')[0].trim()} - learned!`;
      const said = all.split(',')[0].trim();
      return said || 'done';
    },
  },
};
</script>
